import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getInspections } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import AddInspectionModal from '../components/modals/AddInspectionModal';

export default function Inspections() {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadInspections();
  }, []);

  const loadInspections = async () => {
    try {
      const data = await getInspections();
      setInspections(data);
    } catch (error) {
      toast.error('Failed to load inspections');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div data-testid="inspections-page" className="max-w-7xl mx-auto animate-fade-in">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Inspections</h1>
            <p className="text-gray-600">Schedule and manage property inspections</p>
          </div>
          <Button data-testid="schedule-inspection-btn" className="gap-2" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4" />
            Schedule Inspection
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : inspections.length === 0 ? (
          <Card data-testid="empty-state">
            <CardContent className="flex flex-col items-center justify-center py-20">
              <Calendar className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No inspections scheduled</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {inspections.map((inspection) => (
              <Card key={inspection.id} data-testid={`inspection-card-${inspection.id}`} className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-gray-900 capitalize">{inspection.inspection_type}</span>
                    <span className={`badge ml-auto ${inspection.status === 'completed' ? 'badge-success' : inspection.status === 'cancelled' ? 'badge-danger' : 'badge-info'}`}>
                      {inspection.status}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      {format(new Date(inspection.scheduled_date), 'MMM dd, yyyy HH:mm')}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <AddInspectionModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={loadInspections}
        />
      </div>
    </Layout>
  );
}